import React, { useEffect, useState } from 'react';
import { useSystemAdmin } from '../../hooks/useSystemAdmin';
import './SystemAdminPage.css';

const USER_TYPES = ['student', 'supervisor', 'admin', 'promptengineer'];
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const SystemAdminPage = ({ callerUserId }) => {
  const admin = useSystemAdmin(callerUserId);
  const [tab, setTab] = useState('users');

  // User-management tab state
  const [searchTerm, setSearchTerm] = useState('');

  // Assignment tab state
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [pendingAssign, setPendingAssign] = useState(null); // {user, existingSupervisor}

  // Add-user tab state
  const blankNewUser = {
    user_name: '', first_name: '', last_name: '',
    user_type: 'supervisor', user_status: 'active',
  };
  const [newUser, setNewUser] = useState(blankNewUser);
  const [matchedOrg, setMatchedOrg] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    admin.loadSupervisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerUserId]);

  // Resolve organization from email domain as the admin types
  useEffect(() => {
    const email = newUser.user_name.trim();
    if (!EMAIL_RE.test(email)) {
      setMatchedOrg(null);
      return;
    }
    const t = setTimeout(async () => {
      const org = await admin.lookupOrganization(email);
      setMatchedOrg(org);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newUser.user_name]);

  // Debounced live search for the "Manage User Types" tab
  useEffect(() => {
    const t = setTimeout(() => admin.searchUsers(searchTerm), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Debounced live search for the "Assign" tab
  useEffect(() => {
    const t = setTimeout(() => {
      if (assignSearch.trim()) {
        admin.searchAssignUsers(assignSearch);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignSearch]);

  useEffect(() => {
    if (selectedSupervisorId) {
      admin.loadAssignedUsers(selectedSupervisorId);
    } else {
      admin.loadAssignedUsers(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupervisorId]);

  const handleTypeChange = async (user, newType) => {
    if (user.user_type === newType) return;
    if (!window.confirm(
      `Change ${user.user_first_name} ${user.user_last_name} from "${user.user_type}" to "${newType}"?`
    )) return;
    const res = await admin.updateUserType(user.user_id, newType);
    if (res.success) {
      admin.searchUsers(searchTerm);
      admin.loadSupervisors();
    } else {
      alert(res.error);
    }
  };

  const beginAssign = async (user) => {
    if (!selectedSupervisorId) {
      alert('Please select a supervisor first.');
      return;
    }
    if (String(user.user_id) === String(selectedSupervisorId)) {
      alert('A supervisor cannot be assigned to themselves.');
      return;
    }
    const res = await admin.getUsersSupervisor(user.user_id);
    if (!res.success) { alert(res.error); return; }
    const existing = res.supervisor;
    if (existing && String(existing.user_id) === String(selectedSupervisorId)) {
      alert('User is already assigned to this supervisor.');
      return;
    }
    setPendingAssign({ user, existingSupervisor: existing });
  };

  const confirmAssign = async () => {
    if (!pendingAssign) return;
    const res = await admin.assignUser(pendingAssign.user.user_id, selectedSupervisorId);
    if (res.success) {
      setPendingAssign(null);
      admin.loadAssignedUsers(selectedSupervisorId);
    } else {
      alert(res.error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(newUser.user_name.trim())) {
      alert('Please enter a valid email address.');
      return;
    }
    if (!matchedOrg) {
      alert('No organization matches this email domain.');
      return;
    }
    setCreateResult(null);
    setCreating(true);
    const res = await admin.createUser(newUser);
    setCreating(false);
    if (res.success) {
      setCreateResult(res.data);
      setNewUser(blankNewUser);
      setMatchedOrg(null);
      admin.searchUsers(searchTerm);
      admin.loadSupervisors();
    } else {
      alert(res.error);
    }
  };

  const handleUnassign = async (user) => {
    if (!window.confirm(
      `Remove ${user.user_first_name} ${user.user_last_name} from this supervisor?`
    )) return;
    const res = await admin.unassignUser(user.user_id, selectedSupervisorId);
    if (res.success) {
      admin.loadAssignedUsers(selectedSupervisorId);
    } else {
      alert(res.error);
    }
  };

  const assignedIds = new Set(admin.assignedUsers.map((u) => u.user_id));
  const assignCandidates = admin.assignSearchResults.filter(
    (u) => !assignedIds.has(u.user_id)
      && String(u.user_id) !== String(selectedSupervisorId),
  );
  const filterStr = assignSearch.trim().toLowerCase();
  const filteredAssignedUsers = filterStr
    ? admin.assignedUsers.filter((u) => {
        const hay = `${u.user_first_name || ''} ${u.user_last_name || ''} ${u.user_name || ''}`.toLowerCase();
        return hay.includes(filterStr);
      })
    : admin.assignedUsers;

  return (
    <div className="sysadmin-container">
      <h2>System Admin</h2>

      <div className="sysadmin-tabs">
        <button
          className={tab === 'users' ? 'active' : ''}
          onClick={() => setTab('users')}
        >
          Manage User Types
        </button>
        <button
          className={tab === 'assign' ? 'active' : ''}
          onClick={() => setTab('assign')}
        >
          Assign Users to Supervisors
        </button>
        <button
          className={tab === 'add' ? 'active' : ''}
          onClick={() => { setTab('add'); setCreateResult(null); }}
        >
          Add User
        </button>
      </div>

      {admin.error && <div className="sysadmin-error">{admin.error}</div>}

      {tab === 'users' && (
        <div className="sysadmin-panel">
          <div className="sysadmin-search">
            <input
              type="text"
              placeholder="Type to search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <table className="sysadmin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>User Type</th>
              </tr>
            </thead>
            <tbody>
              {admin.users.map((u) => (
                <tr key={u.user_id}>
                  <td>{u.user_id}</td>
                  <td>{u.user_first_name} {u.user_last_name}</td>
                  <td>{u.user_name}</td>
                  <td>{u.user_status}</td>
                  <td>
                    <select
                      value={u.user_type || ''}
                      onChange={(e) => handleTypeChange(u, e.target.value)}
                    >
                      {USER_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {!admin.users.length && !admin.loading && (
                <tr><td colSpan="5">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'assign' && (
        <div className="sysadmin-panel">
          <div className="sysadmin-row">
            <label>Supervisor:&nbsp;</label>
            <select
              value={selectedSupervisorId}
              onChange={(e) => setSelectedSupervisorId(e.target.value)}
            >
              <option value="">-- select a supervisor --</option>
              {admin.supervisors.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {s.user_first_name} {s.user_last_name} ({s.user_name})
                </option>
              ))}
            </select>
          </div>

          {selectedSupervisorId && (
            <>
              <div className="sysadmin-search">
                <input
                  type="text"
                  placeholder="Type to search users by name or email..."
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <h3>Currently assigned</h3>
              <table className="sysadmin-table">
                <thead>
                  <tr><th>ID</th><th>Name</th><th>Email</th><th></th></tr>
                </thead>
                <tbody>
                  {filteredAssignedUsers.map((u) => (
                    <tr key={u.user_id}>
                      <td>{u.user_id}</td>
                      <td>{u.user_first_name} {u.user_last_name}</td>
                      <td>{u.user_name}</td>
                      <td>
                        <button onClick={() => handleUnassign(u)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                  {!filteredAssignedUsers.length && (
                    <tr><td colSpan="4">
                      {assignSearch.trim() ? 'No matches in current assignments.' : 'No users assigned.'}
                    </td></tr>
                  )}
                </tbody>
              </table>

              <h3>Add a user to this supervisor</h3>
              {assignSearch.trim() ? (
                <table className="sysadmin-table">
                  <thead>
                    <tr><th>ID</th><th>Name</th><th>Email</th><th>Type</th><th></th></tr>
                  </thead>
                  <tbody>
                    {assignCandidates.map((u) => (
                      <tr key={u.user_id}>
                        <td>{u.user_id}</td>
                        <td>{u.user_first_name} {u.user_last_name}</td>
                        <td>{u.user_name}</td>
                        <td>{u.user_type}</td>
                        <td>
                          <button onClick={() => beginAssign(u)}>Assign</button>
                        </td>
                      </tr>
                    ))}
                    {!assignCandidates.length && (
                      <tr><td colSpan="5">No matches.</td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <p className="sysadmin-hint">Start typing above to find a user to assign.</p>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'add' && (
        <div className="sysadmin-panel">
          <form className="sysadmin-form" onSubmit={handleCreateUser}>
            <label>
              Email (user_name)
              <input
                type="email"
                required
                value={newUser.user_name}
                onChange={(e) => setNewUser({ ...newUser, user_name: e.target.value })}
              />
              {newUser.user_name && !EMAIL_RE.test(newUser.user_name.trim()) && (
                <span className="sysadmin-field-error">Enter a valid email address</span>
              )}
            </label>
            <label>
              First name
              <input
                type="text"
                required
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              />
            </label>
            <label>
              Last name
              <input
                type="text"
                required
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              />
            </label>
            <label>
              Organization
              <input
                type="text"
                readOnly
                value={
                  matchedOrg
                    ? `${matchedOrg.full_name}${matchedOrg.short_name ? ` (${matchedOrg.short_name})` : ''}`
                    : ''
                }
                placeholder="Auto-filled from email domain"
              />
              {newUser.user_name && EMAIL_RE.test(newUser.user_name.trim()) && !matchedOrg && (
                <span className="sysadmin-field-error">
                  No organization matches this email domain
                </span>
              )}
            </label>
            <label>
              User type
              <select
                value={newUser.user_type}
                onChange={(e) => setNewUser({ ...newUser, user_type: e.target.value })}
              >
                {USER_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={newUser.user_status}
                onChange={(e) => setNewUser({ ...newUser, user_status: e.target.value })}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="pending">pending</option>
              </select>
            </label>
            <div>
              <button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </form>

          {createResult && (
            <div className="sysadmin-success">
              <p>
                <strong>User created.</strong> Temporary password (share this once,
                the user should change it on first login):
              </p>
              <code className="sysadmin-password">{createResult.temp_password}</code>
            </div>
          )}
        </div>
      )}

      {pendingAssign && (
        <div className="sysadmin-modal-overlay">
          <div className="sysadmin-modal">
            {pendingAssign.existingSupervisor ? (
              <p>
                <strong>
                  {pendingAssign.user.user_first_name} {pendingAssign.user.user_last_name}
                </strong> is currently assigned to{' '}
                <strong>
                  {pendingAssign.existingSupervisor.user_first_name}{' '}
                  {pendingAssign.existingSupervisor.user_last_name}
                </strong>. Reassigning will remove that assignment. Continue?
              </p>
            ) : (
              <p>
                Assign <strong>
                  {pendingAssign.user.user_first_name} {pendingAssign.user.user_last_name}
                </strong> to the selected supervisor?
              </p>
            )}
            <div className="sysadmin-modal-buttons">
              <button onClick={confirmAssign}>Confirm</button>
              <button onClick={() => setPendingAssign(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdminPage;
